import { Checkbox } from "../../Checkbox/Checkbox";
import { Dialog } from "../../Dialog/Dialog";
import { Form } from "../../Form/Form";
import { FormItem } from "../../FormItem/FormItem";
import { TextInput } from "../../TextInput/TextInput";
import { Toolbar } from "../../Toolbar/Toolbar";
import { ValidationError } from "../../ValidationError/ValidationError";
import { IFormHandler, useFormHandler } from "client/Forms";
import { useDataInputDialog } from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	inline,
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { IValidationConfiguration } from "client/Validation/builder";
import { IValidationRuleWithBuilder } from "client/Validation/rules/triggers";

const {
	Header,
	Body,
	Footer
} = Dialog;

export type EditLinkDialogProps = {
	initialData?: EditLinkInitialModel;
	onClose(): void;
	onSave(updatedData: EditLinkResult): void;
};

export const EditLinkDialog = (props: EditLinkDialogProps): ReactElement => {
	const {
		initialData,
		onClose,
		onSave
	} = props;

	const [formData, setFormData] = useState<EditLinkResult>(
		initialData
			? {
				text: initialData.text,
				href: initialData.href,
				shouldOpenInNewWindow: initialData.shouldOpenInNewWindow
			}
			: {
				text: "",
				href: "https://",
				shouldOpenInNewWindow: true
			}
	);

	const doesSupportTextEditing: boolean = initialData?.doesSupportTextEditing ?? true;
	const areHashUrlsAllowed: boolean = initialData?.areHashUrlsAllowed ?? false;

	const validationConfiguration: IValidationConfiguration<EditLinkResult> = useMemo(
		() => {
			const result: IValidationConfiguration<EditLinkResult> = staticValidationConfiguration.clone();

			if (doesSupportTextEditing) {
				result.forScalar("text")
					.add(
						isNotNullNorUndefined(),
						isNotEmpty()
					);
			}

			const baseIsUrlRule: IValidationRuleWithBuilder<string> = isUrl({ allowHttpsOnly: false });

			if (areHashUrlsAllowed) {
				result.forScalar("href")
					.add(
						inline(
							async (value, context) => {
								const baseResult: InputValidationResult = await baseIsUrlRule.validate(value, context);

								if (baseResult.kind === InputValidationResultKind.Valid) {
									return baseResult;
								}

								if (isValidHashUrl(value)) {
									return {
										kind: InputValidationResultKind.Valid
									};
								}

								return baseResult;
							}
						)
					);
			} else {
				result.forScalar("href")
					.add(baseIsUrlRule);
			}

			return result;
		},
		[
			doesSupportTextEditing,
			areHashUrlsAllowed
		]
	);

	const formHandler: IFormHandler<EditLinkResult> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			const validationResult: InputValidationResult = await formHandler.validation
				.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			onSave(formData);
		},
		[
			formData,
			formHandler,
			onSave
		]
	);

	return (
		<Dialog trackingId="edit-link-dialog">
			<Header
				title={localize("_InsertHyperlink")}
				onClose={onClose}
			/>
			<Body>
				<Form onSubmit={submitHandler}>
					{doesSupportTextEditing && (
						<FormItem label={localize("_Text")}>
							<TextInput
								{...formHandler.for("text").attachToTextInput()}
								autoFocus="focus"
							/>
							<ValidationError {...formHandler.for("text").attachToValidationError()} />
						</FormItem>
					)}
					<FormItem label={localize("_Webpage")}>
						<TextInput {...formHandler.for("href").attachToTextInput()} />
						<ValidationError {...formHandler.for("href").attachToValidationError()} />
					</FormItem>
					<FormItem>
						<Checkbox
							{...formHandler.for("shouldOpenInNewWindow").attachToCheckbox()}
							text={localize("_OpenInNewTab")}
						/>
						<ValidationError {...formHandler.for("shouldOpenInNewWindow").attachToValidationError()} />
					</FormItem>
				</Form>
			</Body>
			<Footer>
				<Toolbar>
					<Toolbar.Button
						trackingId="done"
						kind="primary"
						text={localize("_Done")}
						onClick={submitHandler}
					/>
					<Toolbar.Button
						trackingId="cancel"
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Footer>
		</Dialog>
	);
};
EditLinkDialog.displayName = "EditLinkDialog";

export type EditLinkInitialModel = {
	text: string;
	doesSupportTextEditing: boolean;
	href: string;
	shouldOpenInNewWindow: boolean;
	areHashUrlsAllowed: boolean;
};

export type EditLinkResult = {
	text: string;
	href: string;
	shouldOpenInNewWindow: boolean;
};

export function useEditLinkDialog(): [
	element: ReactElement | null,
	open: (initialData?: EditLinkInitialModel) => Promise<TryResult<EditLinkResult>>
] {
	return useDataInputDialog(
		(initialData, onClose, onSave) => (
			<EditLinkDialog
				initialData={initialData}
				onClose={onClose}
				onSave={onSave}
			/>
		)
	);
}

const staticValidationConfiguration = createValidationConfiguration<EditLinkResult>();

staticValidationConfiguration.forScalar("href")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

function isValidHashUrl(hashUrl: string): boolean {
	if (!hashUrl.startsWith("#")) {
		return false;
	}

	try {
		const resultUrl: URL = new URL(hashUrl, "https://example.com/");

		return resultUrl.hash === hashUrl;
	} catch(e) {
		if (e instanceof TypeError) {
			return false;
		}

		throw e;
	}
}